import React, { useCallback, useEffect, useState } from 'react';
import { Form, Select, Input, Button, Typography, message, Alert } from 'antd';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import apiCalls from 'src/api/apiCalls';
import Loader from '../loader.component';
import { NumericFormat } from 'react-number-format';


const { Option } = Select;
const { Title } = Typography;

const EnergyRechargeForm = (props) => {
  const [form] = Form.useForm();
  const [transactionQuantity, setTransactionQuantity] = useState(0);
  const [addressTypes, setAddressTypes] = useState([]);
  const [loader, setLoader] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [btnLoader, setBtnLoader] = useState(false);
  const { availableCredit, setIsEnergyGridRefresh } = props?.propsData;


  const onFinish = useCallback(async (values) => {
    if (values?.transaction <= 0) {
      setErrorMessage('Transaction quantity must be greater than 0.');
      return;
    }
    if (values?.transaction > 5) {
      setErrorMessage('Transaction quantity must be below 5 or equal.');
      return;
    }
    try {
      setErrorMessage(null);
      setBtnLoader(true);
      const obj = {
        address: values?.address,
        count: values?.transaction ? parseInt(values.transaction, 10) : 0,
      };
      const response = await apiCalls.saveRechargeEnergy(obj);
      if (response.ok) {
        message.success('Recharge successfully!');
        form.resetFields();
        setTransactionQuantity('');
        setErrorMessage(null);
        setBtnLoader(false);
        setIsEnergyGridRefresh(true);
      } else {
        setBtnLoader(false);
        setErrorMessage(response.error);
      }
    } catch (error) {
      setErrorMessage(error.message || 'An unexpected error occurred.');
      setBtnLoader(false);
    }
  }, [form]);

  const updateTransactionAndValidate = useCallback((newValue) => {
    const newValueStr = String(newValue);
    setTransactionQuantity(newValueStr);
    form.setFieldsValue({ transaction: newValueStr });
    form.validateFields(['transaction']);
  }, [form]);

  const handleDecrement = useCallback(() => {
    const currentValueStr = form.getFieldValue('transaction') || '0';
    const currentNum = /^\d+$/.test(currentValueStr) ? parseInt(currentValueStr, 10) : 0;
    const newValue = Math.max(0, currentNum - 1); // Prevent negative values
    updateTransactionAndValidate(newValue);
  }, [form, updateTransactionAndValidate]);

  const handleIncrement = useCallback(() => {
    const currentValueStr = form.getFieldValue('transaction') || '0';
    const currentNum = /^\d+$/.test(currentValueStr) ? parseInt(currentValueStr, 10) : 0;
    const newValue = currentNum + 1; // Allow unrestricted increment
    updateTransactionAndValidate(newValue);
  }, [form, updateTransactionAndValidate]);

  const handleInputChange = useCallback((e) => {
    const inputValue = e.target.value;

    if (inputValue === '') {
      setTransactionQuantity('');
      form.setFieldsValue({ transaction: '' });
      form.validateFields(['transaction']);
      return;
    }
    if (/^\d*$/.test(inputValue)) {
      setTransactionQuantity(inputValue);
      form.setFieldsValue({ transaction: inputValue });
      form.validateFields(['transaction']);
    } else {
      form.setFieldsValue({ transaction: transactionQuantity });
      form.validateFields(['transaction']);
    }
  }, [form, transactionQuantity]);


  const getAddressTypes = useCallback(async () => {
    setErrorMessage(null);
    setLoader(true);
    try {
      const response = await apiCalls.getAddressTypes();
      if (response.ok) {
        setAddressTypes(response?.data || []);
        setErrorMessage(null);
        setLoader(false);
      }
      else {
        setLoader(false);
        setErrorMessage(response?.error);
      }
    } catch (error) {
      setErrorMessage(error);
      setLoader(false);
    }
  }, []);

  useEffect(() => {
    getAddressTypes();
  }, [getAddressTypes]);

  const handleChange = useCallback((value) => {
    form.setFieldsValue({ address: value });
    form.validateFields(['address']);
  }, [form]);

  const closeErrorMessage = useCallback(() => {
    setErrorMessage(null);
  }, []);


  return (
    <div className="energy-recharge-container ">
      <Title level={4} className="text-2xl font-semibold !text-titleColor mb-2">Energy Recharge</Title>
      {errorMessage && (
        <Alert
          type="error"
          description={errorMessage}
          onClose={closeErrorMessage}
          showIcon
          closable className='mb-2' />
      )}
      {loader ? <Loader /> : <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
      >
        <Form.Item
          className="custom-input !mb-3"
          label="Address"
          name="address"
          rules={[{ required: true, message: 'Is required!' }]}
        >
          <Select
            className=""
            placeholder={`Select Address`}
            onChange={handleChange}
          >
            {addressTypes?.map((item) => (
              <Option value={item?.address} key={item?.id}>{item?.address}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          className="custom-input !mb-1"
          label="Transaction"
          name="transaction"
          rules={[
            { required: true, message: 'Is required!' },
            {
              validator: (_, value) => {
                const stringValue = String(value ?? '');

                if (stringValue.trim() === '') {
                  return Promise.resolve();
                }
                if (!/^\d+$/.test(stringValue)) {
                  return Promise.reject('Please enter numbers only.');
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            placeholder="Please enter"
            className="custom-energy-input"
            prefix={
              <Button
                icon={<MinusOutlined className="text-primaryColor font-semibold" />}
                onClick={handleDecrement}
                type="button"
              />
            }
            suffix={
              <Button
                icon={<PlusOutlined className="text-primaryColor font-semibold" />}
                onClick={handleIncrement}
                type="button"
              />
            }
            onChange={handleInputChange}
            autoComplete="off"
          />
        </Form.Item>

        <div className="flex justify-between items-center mb-6">
          <p className="text-base text-paraColor font-medium">{`Max Recharge Quantity: `}
            <NumericFormat
              value={availableCredit?.data?.creditLimit || 0}
              displayType="text"
              thousandSeparator
              decimalScale={4}
            />
          </p>
          <p className="text-base text-paraColor font-medium">{`Available Credit:  `}
            <NumericFormat
              value={availableCredit?.data?.availableCredit || 0}
              displayType="text"
              thousandSeparator
              decimalScale={4}
            /></p>
        </div>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            className="primary-btn w-full"
            loading={btnLoader}
          >
            Recharge
          </Button>
        </Form.Item>

        <div className="disclaimer-container">
          <p className="text-base text-paraColor font-normal">Energy must be used within <span className="text-primaryColor font-medium">10 minutes</span>. otherwise, it will be reclaimed.</p>
        </div>
      </Form>}
    </div>
  );
};

export default EnergyRechargeForm;
